import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { matchPath } from 'react-router';
import {
  fetchOdpContentDocuments,
  type OdpContentDocument,
} from './odpContentFeed';

type AiChatWidgetContextBuilderArgs = {
  pathname: string;
  params: Readonly<Record<string, string | undefined>>;
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  signal?: AbortSignal;
};

type AiChatWidgetContextBuilder = (
  args: AiChatWidgetContextBuilderArgs,
) => Promise<string | null>;

// Keeps the hidden context small enough to stay usable as a single chat turn.
const MAX_PAGE_CONTENT_CHARS = 20000;

const PAGE_CONTENT_PREAMBLE = [
  'This is the text the user is currently seeing and reading on this page.',
  'You can use it to summarize it, extract the key ideas, or answer questions about it,',
  'but only use it if the user actually asks for something related to it and start with something like "According to this page"',
  'It describes only the page the user is on right now and stops being valid as soon as they navigate away.',
].join(' ');

/** Path segments under /journeys that are not journey slugs. */
const RESERVED_JOURNEY_SEGMENTS = new Set(['admin', 'workflows']);

async function buildTechDocsContext(
  args: AiChatWidgetContextBuilderArgs,
): Promise<string | null> {
  const { namespace, kind, name } = args.params;
  if (!namespace || !kind || !name) {
    return null;
  }

  const resourceRef = `${kind}:${namespace}/${name}`;
  const readerPath = args.params['*'];
  const documents = await fetchOdpContentDocuments({
    discoveryApi: args.discoveryApi,
    fetchApi: args.fetchApi,
    type: 'techdocs',
    resourceRef,
    // The site root has an empty reader path; 'index' is its canonical equivalent.
    pageLocation: readerPath?.length ? readerPath : 'index',
    limit: 1,
    signal: args.signal,
  });

  return formatPageContent(`TechDocs page of ${resourceRef}`, documents);
}

async function buildAcademyContext(
  args: AiChatWidgetContextBuilderArgs,
): Promise<string | null> {
  const courseId = args.params.courseId;
  if (!courseId) {
    return null;
  }

  const resourceRef = `academy:default/${courseId}`;
  const documents = await fetchOdpContentDocuments({
    discoveryApi: args.discoveryApi,
    fetchApi: args.fetchApi,
    type: 'academy',
    resourceRef,
    signal: args.signal,
  });

  return formatPageContent(`Academy content ${resourceRef}`, documents);
}

async function buildJourneyContext(
  args: AiChatWidgetContextBuilderArgs,
): Promise<string | null> {
  const journeyId = args.params.journeyId;
  if (!journeyId || RESERVED_JOURNEY_SEGMENTS.has(journeyId)) {
    return null;
  }
  return buildJourneysDocument(args, `journey:default/${journeyId}`);
}

async function buildJourneyWorkflowContext(
  args: AiChatWidgetContextBuilderArgs,
): Promise<string | null> {
  const workflowId = args.params.workflowId;
  if (!workflowId) {
    return null;
  }
  return buildJourneysDocument(args, `workflow:default/${workflowId}`);
}

async function buildJourneysDocument(
  args: AiChatWidgetContextBuilderArgs,
  resourceRef: string,
): Promise<string | null> {
  const documents = await fetchOdpContentDocuments({
    discoveryApi: args.discoveryApi,
    fetchApi: args.fetchApi,
    type: 'journeys',
    resourceRef,
    signal: args.signal,
    limit: 1,
  });

  return formatPageContent(`Journeys content ${resourceRef}`, documents);
}

// Add an entry here for any page that should feed richer context into the chat widget.
// First matching path wins, so more specific paths must come first.
const AI_CHAT_WIDGET_CONTEXT_PROVIDERS: Array<{
  path: string;
  build: AiChatWidgetContextBuilder;
}> = [
  {
    path: '/docs/:namespace/:kind/:name/*',
    build: buildTechDocsContext,
  },
  {
    path: '/academy/course/:courseId',
    build: buildAcademyContext,
  },
  {
    path: '/journeys/workflows/:workflowId',
    build: buildJourneyWorkflowContext,
  },
  {
    path: '/journeys/:journeyId',
    build: buildJourneyContext,
  },
];

/**
 * Builds the hidden context for the page the user is currently on.
 *
 * The result is always page-scoped: the caller replaces it on every navigation,
 * so neither `additionalContext` nor fetched page content leaks to another page.
 */
export async function resolveAiChatWidgetContext(options: {
  pathname: string;
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  additionalContext?: string;
  signal?: AbortSignal;
}): Promise<string> {
  const sections = [`Page: ${options.pathname}`];

  const additionalContext = options.additionalContext?.trim();
  if (additionalContext) {
    sections.push(additionalContext);
  }

  const pageContent = await buildPageContent(options);
  if (pageContent) {
    sections.push(pageContent);
  }

  return sections.join('\n\n');
}

async function buildPageContent(options: {
  pathname: string;
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  signal?: AbortSignal;
}): Promise<string | null> {
  for (const provider of AI_CHAT_WIDGET_CONTEXT_PROVIDERS) {
    const match = matchPath(
      { path: provider.path, end: false },
      options.pathname,
    );
    if (!match) {
      continue;
    }
    try {
      return await provider.build({
        pathname: options.pathname,
        params: match.params,
        discoveryApi: options.discoveryApi,
        fetchApi: options.fetchApi,
        signal: options.signal,
      });
    } catch {
      // Ignore fetch/parse failures and fall back to the plain page context.
      return null;
    }
  }

  return null;
}

function formatPageContent(
  source: string,
  documents: readonly OdpContentDocument[],
): string | null {
  const sections: string[] = [];
  let budget = MAX_PAGE_CONTENT_CHARS;

  for (const document of documents) {
    if (budget <= 0) {
      break;
    }
    const text =
      document.text.length > budget
        ? `${document.text.slice(0, budget)}…`
        : document.text;
    budget -= text.length;
    sections.push(`## ${document.title}\n${document.location}\n\n${text}`);
  }

  if (sections.length === 0) {
    return null;
  }

  return [PAGE_CONTENT_PREAMBLE, `Source: ${source}`, ...sections].join('\n\n');
}
